import { useEffect, useState } from 'react';
import { v1 } from '@/infrastructure/api/v1/client';
import { listAll, listAllIn } from '@/infrastructure/api/v1/lists';
import type { TemplateDifficulty } from './types';

export const TEMPLATE_MIN_UNIQUE_ASSETS = 5;

const ANCHOR_CATEGORIES = new Set(['immersal', 'image_anchor', 'geospatial_anchor']);

// Anchors and system utility instances don't count toward the unique-asset
// requirement; everything the creator placed as content does.
const NON_CONTENT_CATEGORIES = new Set([
  ...ANCHOR_CATEGORIES,
  'place_anywhere',
  'camera',
  'tap_to_place',
  'locomotion',
]);

export interface TemplateSceneSignals {
  loading: boolean;
  hasAnchor: boolean;
  uniqueAssetCount: number;
  hasMask: boolean;
  hasAnimation: boolean;
  suggestedDifficulty: TemplateDifficulty | null;
}

const LOADING: TemplateSceneSignals = {
  loading: true,
  hasAnchor: false,
  uniqueAssetCount: 0,
  hasMask: false,
  hasAnimation: false,
  suggestedDifficulty: null,
};

// If the reads fail we must not wrongly block a publish, so the error state
// passes every requirement and just suggests nothing.
const FAILED_OPEN: TemplateSceneSignals = {
  loading: false,
  hasAnchor: true,
  uniqueAssetCount: TEMPLATE_MIN_UNIQUE_ASSETS,
  hasMask: false,
  hasAnimation: false,
  suggestedDifficulty: null,
};

// Reads the scene's contents to (a) check the template acceptance requirements
// (an anchor and at least TEMPLATE_MIN_UNIQUE_ASSETS unique assets) before the
// creator spends time on a submission reviewers would bounce, and (b) suggest a
// difficulty: masks read as advanced, keyframe animation as intermediate.
export function useTemplateSceneSignals(
  sceneInstanceId: string | null | undefined,
  enabled: boolean,
): TemplateSceneSignals {
  const [signals, setSignals] = useState<TemplateSceneSignals>(LOADING);

  // Disabling, or losing the scene id, puts the signals back to loading
  const active = enabled && !!sceneInstanceId;
  const [wasActive, setWasActive] = useState(active);
  if (wasActive !== active) {
    setWasActive(active);
    if (!active) setSignals(LOADING);
  }

  useEffect(() => {
    if (!enabled || !sceneInstanceId) return;
    let cancelled = false;
    void (async () => {
      try {
        const { data: rows, error } = await listAll(page =>
          v1.GET('/api/v1/asset-instances', { params: { query: { sceneUniqueId: [sceneInstanceId], ...page } } }),
        );
        if (error) throw error;

        const hasAnchor = rows.some((r) => ANCHOR_CATEGORIES.has(r.category ?? ''));
        const content = rows.filter((r) => !NON_CONTENT_CATEGORIES.has(r.category ?? ''));
        const uniqueAssets = new Set(content.map((r) => r.assetTypeUniqueId ?? `instance:${r.uniqueId}`));
        const hasMask = content.some((r) => r.category === 'mask');

        // Every placed asset carries one default transform keyframe. Animation
        // means the scene has 10 or more keyframes beyond those defaults.
        let hasAnimation = false;
        const contentIds = content.map((r) => r.uniqueId);
        if (contentIds.length > 0) {
          const { data: rowsWithKeyframes, error: kfError } = await listAllIn(contentIds, (assetInstanceId, page) =>
            v1.GET('/api/v1/keyframes', { params: { query: { assetInstanceUniqueId: assetInstanceId, ...page } } }),
          );
          if (kfError) throw kfError;
          const instancesWithKeyframes = new Set(rowsWithKeyframes.map((kf) => kf.assetInstanceUniqueId));
          const extraKeyframes = rowsWithKeyframes.length - instancesWithKeyframes.size;
          hasAnimation = extraKeyframes >= 10;
        }

        if (cancelled) return;
        setSignals({
          loading: false,
          hasAnchor,
          uniqueAssetCount: uniqueAssets.size,
          hasMask,
          hasAnimation,
          suggestedDifficulty: hasMask ? 'advanced' : hasAnimation ? 'intermediate' : 'beginner',
        });
      } catch (err) {
        console.error('useTemplateSceneSignals failed', err);
        if (!cancelled) setSignals(FAILED_OPEN);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, sceneInstanceId]);

  return signals;
}

// The message shown wherever the template path is gated. Null while loading or
// when the story qualifies.
export function templateRequirementsReason(s: TemplateSceneSignals): string | null {
  if (s.loading) return null;
  const missingAnchor = !s.hasAnchor;
  const missingAssets = s.uniqueAssetCount < TEMPLATE_MIN_UNIQUE_ASSETS;
  if (missingAnchor && missingAssets) {
    return `To become a template, a story needs an anchor and at least ${TEMPLATE_MIN_UNIQUE_ASSETS} unique assets. This story has no anchor and ${s.uniqueAssetCount} of ${TEMPLATE_MIN_UNIQUE_ASSETS} assets.`;
  }
  if (missingAnchor) {
    return 'To become a template, a story needs an anchor. Add an Immersal scan, image, or map location first.';
  }
  if (missingAssets) {
    return `To become a template, a story needs at least ${TEMPLATE_MIN_UNIQUE_ASSETS} unique assets. This story has ${s.uniqueAssetCount}.`;
  }
  return null;
}
